import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Bell, CheckCircle2, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = ["Local Plan", "5-Year HLS", "Green Belt", "SHLAA / Call for Sites", "Brownfield Register", "Neighbourhood Plan"];

export default function Watch() {
  const [form, setForm] = useState({ name: "", email: "", postcode: "", frequency: "Quarterly", interests: ["Local Plan", "5-Year HLS"], notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleInterest = (i) => {
    setForm(f => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter(x => x !== i) : [...f.interests, i] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.postcode.trim()) {
      toast.error("Please complete name, email and postcode.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/watch/subscribe`, form);
      setDone(data);
      toast.success("Subscribed.");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="watch-success" className="bg-[#0B0F0E] min-h-[80vh]">
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <CheckCircle2 className="text-[#C9A24B] mx-auto" size={56} />
          <h1 className="font-display text-[#F4EFE6] text-5xl md:text-6xl mt-7 leading-[0.95]">
            You&apos;re on the <span className="italic-gold">watch list.</span>
          </h1>
          <p className="text-[#9BA39B] text-lg mt-6 max-w-xl mx-auto leading-relaxed">
            Thanks {done.name.split(" ")[0]}. We&apos;re now watching planning activity around <span className="text-[#F4EFE6]">{done.postcode}</span>
            {done.region ? <> in <span className="text-[#F4EFE6]">{done.region}</span></> : null} and will email you on a{" "}
            <span className="text-[#C9A24B]">{done.frequency.toLowerCase()}</span> basis.
          </p>
          <p className="text-xs text-[#9BA39B] mt-6 max-w-md mx-auto">
            You can unsubscribe at any time using the link in every email, or contact us directly.
          </p>
          <a href="/" data-testid="watch-home" className="usl-btn-outline mt-10 inline-flex">Return Home</a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="watch-page" className="bg-[#0B0F0E]">
      <Toaster position="top-center" richColors theme="dark" />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-12">
        <div className="overline"><span className="h-line" />Landowner Watch · Free</div>
        <h1 className="font-display text-[#F4EFE6] text-6xl md:text-7xl lg:text-[5.5rem] mt-7 leading-[0.95] max-w-4xl">
          We watch the planning system <span className="italic-gold">for you.</span>
        </h1>
        <p className="text-[#9BA39B] text-lg max-w-3xl mt-9 leading-relaxed">
          Tell us your postcode and we&apos;ll send you a discreet note when planning activity heats up in your area — a new Five-Year Housing Land Supply shortfall, a Local Plan call-for-sites, a Green Belt review, or anything else material to your land.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <form data-testid="watch-form" onSubmit={submit} className="bg-[#11171A] border border-[#F4EFE6]/10 p-8 md:p-12 space-y-7">
            <div className="grid md:grid-cols-2 gap-7">
              <div>
                <label className="overline block mb-2">Your name *</label>
                <input data-testid="watch-name" className="underline-input" value={form.name} onChange={onChange("name")} />
              </div>
              <div>
                <label className="overline block mb-2">Email *</label>
                <input data-testid="watch-email" type="email" className="underline-input" value={form.email} onChange={onChange("email")} />
              </div>
              <div>
                <label className="overline block mb-2">Land postcode *</label>
                <input data-testid="watch-postcode" className="underline-input uppercase tracking-[0.15em]" value={form.postcode} onChange={onChange("postcode")} placeholder="e.g. GL54 3DR" />
              </div>
              <div>
                <label className="overline block mb-2">Frequency</label>
                <select data-testid="watch-frequency" className="underline-input" value={form.frequency} onChange={onChange("frequency")}>
                  <option>Quarterly</option>
                  <option>Monthly</option>
                  <option>On material change</option>
                </select>
              </div>
            </div>

            <div>
              <label className="overline block mb-3">Watch me for (optional)</label>
              <div className="flex flex-wrap gap-2">
                {INTERESTS.map(i => (
                  <button
                    type="button"
                    key={i}
                    data-testid={`watch-interest-${i.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    onClick={() => toggleInterest(i)}
                    className={`text-[11px] tracking-[0.16em] uppercase font-semibold border px-3 py-2 transition-all ${
                      form.interests.includes(i)
                        ? "border-[#C9A24B] bg-[#C9A24B]/10 text-[#C9A24B]"
                        : "border-[#F4EFE6]/15 text-[#9BA39B] hover:border-[#C9A24B]/50"
                    }`}
                  >{i}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="overline block mb-2">Anything else?</label>
              <textarea data-testid="watch-notes" rows={3} className="underline-input resize-none" value={form.notes} onChange={onChange("notes")} placeholder="Approx. acreage, neighbouring villages of interest, anything else you'd like us to watch for." />
            </div>

            <div className="pt-1">
              <button data-testid="watch-submit" type="submit" disabled={loading} className="usl-btn">
                {loading ? "Subscribing..." : "Start Watching My Area"} <Bell size={16} />
              </button>
              <p className="text-xs text-[#9BA39B] mt-4 max-w-xl">Free service. Confidential. Unsubscribe with one click. We never share your details.</p>
            </div>
          </form>
        </div>

        <aside className="lg:col-span-5">
          <div className="bg-[#C9A24B] text-[#0B0F0E] p-8 md:p-10">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold">What you&apos;ll receive</div>
            <ul className="mt-6 space-y-4 text-[15px] leading-relaxed">
              <li className="flex gap-3"><span>—</span><span>A short, plain-English email when planning activity changes around your land.</span></li>
              <li className="flex gap-3"><span>—</span><span>Heads-up on Local Plan consultations &amp; call-for-sites windows in your council.</span></li>
              <li className="flex gap-3"><span>—</span><span>Five-Year Housing Land Supply alerts if your council slips below threshold.</span></li>
              <li className="flex gap-3"><span>—</span><span>Zero pressure to engage further. Useful even if you never sell.</span></li>
            </ul>
            <a href="/insights" data-testid="watch-insights" className="inline-flex items-center gap-2 mt-8 text-xs tracking-[0.22em] uppercase font-semibold link-underline">
              Read our latest insights <ArrowUpRight size={14} />
            </a>
          </div>
        </aside>
      </section>
    </div>
  );
}
